/**
 * KPI-COCKPIT — die Rechenmaschine.
 *
 * Eine reine Funktion: `buildCockpitModel(input) -> CockpitModel`. Kein React,
 * kein Supabase, kein `new Date()` ohne Parameter. Alles, was das Cockpit
 * anzeigt, entsteht hier und ist damit ohne DOM pruefbar — der Grund, aus dem
 * es dieses Modul ueberhaupt gibt (docs/kpi-cockpit-plan.md §3).
 *
 * DIE VIER FESTLEGUNGEN, auf die sich alles andere stuetzt (Plan §2):
 *
 *  1. ERBRACHT ist Menge x AKTUELLER LV-Preis, gebucht auf das LEISTUNGSDATUM
 *     (`cockpitLogic.performedMonth`). Ein Preis-Snapshot je Buchung existiert
 *     nicht — das Journal fuehrt keinen Preis mit —, deshalb bewertet die
 *     Historie zum heutigen Preis. Die Kachel sagt das ausdruecklich.
 *  2. ABGERECHNET kommt aus den BELEGEN (`invoiceStats.revenueNetCents`,
 *     Monat = `invoiceDate`), nicht mehr aus `quantity_billed`. Stornos zaehlen
 *     negativ, Abschlagsabzuege sind abgesetzt, Entwuerfe sind draussen.
 *  3. EIN Zeitraummodell: die Engine rechnet IMMER zwoelf Monate von
 *     `period.year` (plus Dezember des Vorjahres fuer den Trend). Der Zeitraum
 *     waehlt nur aus, welche Monate summiert werden — er definiert keine Kachel
 *     um. Die drei STICHTAGSkacheln (noch abzurechnen, offene Forderungen,
 *     Auftragsbestand) haengen dagegen an `today` und nicht am Zeitraum.
 *  4. Alles in ganzzahligen CENT (`money.ts`), gerundet je Eintrag.
 *
 * WAS HIER NIE PASSIERT: still etwas wegwerfen. Ein Projekt ohne bekannten
 * Verantwortlichen landet in der Zeile „Ohne Zuordnung", ein Eintrag ohne
 * Position wird gezaehlt und gemeldet, eine negative Menge ebenso. Der
 * urspruengliche Befund Nr. 7 war genau das Gegenteil: die Projekte eines
 * ausgeschiedenen Koordinators fielen aus JEDER Kachel, ohne ein Wort.
 */

import type { InvoiceStatus, ServiceTransaction, UserProfile } from '../types';
import { assignmentLabel } from './assignmentLabels';
// Das Deckungsobjekt liest DIESELBE Funktion wie der Forecast: die Spalte
// heisst `flat_rate`, der Blob-Bestand fuehrt sie als `flatRate`. Las die
// Engine nur die Spalte, sah der Forecast bei einer camelCase-Zeile eine
// Deckung, die die Engine nicht kannte — geplant mit 0 Cent, bewertet mit
// vollem Preis.
import { flatRateOf } from './cockpitForecast';
import {
    altQuantity,
    calendarDay,
    daysInMonth,
    entryCents,
    isMigrationBulkBackfill,
    isWorkEntry,
    monthKey,
    performedDateIso,
    periodMonthIndices,
    usesAppliedQuantity,
} from './cockpitLogic';
import type {
    BilledDetail,
    CockpitInput,
    CockpitModel,
    CockpitPeriod,
    CockpitProjectRow,
    CockpitServiceRow,
    CockpitTiles,
    CockpitWarning,
    CockpitWarningKind,
    CustomerRow,
    EmployeeRow,
    MonthCell,
    MonthSeries,
    PerformedDetail,
    ProjectRow,
    ServiceTypeRow,
} from './cockpitTypes';
import {
    bookedFlatMonths,
    flatMonthKey,
    isFlatRateService,
    resolveFlatCoverage,
    type FlatCoverageServiceLike,
} from './flatRateLogic';
import { daysPastDue, effectiveInvoiceStatus, openAmountCents } from './invoiceLogic';
import { isIssuedInvoice, revenueNetCents } from './invoiceStats';
import { centsFromEuro } from './money';
import { contractedQuantityOfRow } from './nepPosition';
import { getProjectFinancials, inspectionProofMatcher, type ProjectRowLike } from './projectLv';
import { getAusfuehrungsVertreter } from './projectRepresentatives';
import { isHourlyUnit } from './serviceUnits';
import { combineWorkload } from './cockpitWorkload';
import { istUebungsprojekt } from './uebung/uebungService';

// ═══════════════════════════════════════════════════════════════════════════
// Kleinkram — jede Zahl durch `Number(x) || 0`, nie NaN
// ═══════════════════════════════════════════════════════════════════════════

/** Die Zeile „Ohne Zuordnung" der Team-Ansicht. */
export const UNASSIGNED = 'unassigned';

/** Auftraggeber-Ersatzname, wenn die Stammdaten keinen hergeben. */
export const NO_CUSTOMER = 'Ohne Auftraggeber';

/**
 * Endliche Zahl oder 0 — die EINZIGE Art, wie in dieser Datei eine Zahl aus
 * einer Supabase-Zeile oder aus `kpi_config` entsteht.
 *
 * `Number(x) || 0` reichte nicht: es faengt NaN (`Number('abc')`), laesst aber
 * `Infinity` durch. Ein einziger kaputter Wert — ein Offset aus `kpi_config`,
 * ein `unit_price` als `1e400` — machte damit eine Monatssumme unendlich, und
 * von dort aus wurde aus jeder Differenz und jedem Anteil ein NaN: Kacheln,
 * Realisierung und Team-Tabelle zeigten „NaN €", ohne dass irgendwo stand,
 * welche Zeile es war. Nicht endlich heisst deshalb: zaehlt nicht.
 */
const num = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const zeroMonths = (): MonthSeries => Array.from({ length: 12 }, () => 0);

const emptyCell = (): MonthCell => ({
    performedCents: 0,
    billedCents: 0,
    forecastCents: 0,
    inspections: 0,
});

/** Gueltiger Kalendertag als `JJJJ-MM-TT`, sonst `null`. Nie `new Date(...)`. */
const isoDay = (value: unknown): string | null => {
    const day = calendarDay(text(value));
    if (!day) return null;
    return `${String(day.year).padStart(4, '0')}-${String(day.month + 1).padStart(2, '0')}-${String(day.day).padStart(2, '0')}`;
};

const fullName = (user: UserProfile): string =>
    `${text(user.first_name)} ${text(user.last_name)}`.replace(/\s+/g, ' ').trim();

const nameKey = (value: unknown): string => text(value).replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Status, in denen ein Beleg eine FORDERUNG ist — wortgleich zu
 * `invoiceStats` (dort `OWING_STATUSES`, nicht exportiert).
 *
 * `DISPUTED` gehoert dazu: eine bestrittene Rechnung ist umstritten, aber
 * gebucht und unbezahlt. `PARTIALLY_CREDITED` ebenfalls — nach einer
 * Teilgutschrift steht der Rest weiter offen. Wer die beiden auslaesst, meldet
 * eine zu niedrige Forderungssumme.
 */
const OWING_STATUSES: ReadonlySet<InvoiceStatus> = new Set<InvoiceStatus>([
    'ISSUED',
    'SENT',
    'PARTIALLY_PAID',
    'PARTIALLY_CREDITED',
    'OVERDUE',
    'DISPUTED',
]);

// ═══════════════════════════════════════════════════════════════════════════
// Warnungen — gebuendelt je Art und Projekt, nie als Zeilenflut
// ═══════════════════════════════════════════════════════════════════════════

interface WarnKey {
    kind: CockpitWarningKind;
    projectId?: string;
    projectName?: string;
}

/**
 * Sammelt Warnungen und fasst sie je (Art, Projekt) zusammen.
 *
 * Warum zusammengefasst: bei 20.000 Protokollzeilen ist „847 Eintraege ohne
 * Position in Projekt X" eine Aussage, 847 Einzelmeldungen sind Rauschen, in
 * dem die eine wichtige Meldung untergeht.
 */
class WarningBag {
    private readonly rows = new Map<string, CockpitWarning>();

    add(key: WarnKey, count: number, cents: number, text_: (count: number, cents: number) => string): void {
        const id = `${key.kind}|${key.projectId ?? ''}`;
        const current = this.rows.get(id);
        if (current) {
            current.count = (current.count ?? 0) + count;
            current.cents = (current.cents ?? 0) + cents;
            current.text = text_(current.count ?? 0, current.cents ?? 0);
            return;
        }
        this.rows.set(id, {
            kind: key.kind,
            ...(key.projectId ? { projectId: key.projectId } : {}),
            ...(key.projectName ? { projectName: key.projectName } : {}),
            count,
            cents,
            text: text_(count, cents),
        });
    }

    /** Eine fertige Warnung von aussen (Forecast) uebernehmen. */
    push(warning: CockpitWarning): void {
        const id = `${warning.kind}|${warning.projectId ?? ''}|${warning.text}`;
        if (!this.rows.has(id)) this.rows.set(id, { ...warning });
    }

    list(): CockpitWarning[] {
        return [...this.rows.values()].sort(
            (a, b) => a.kind.localeCompare(b.kind) || (a.projectName ?? '').localeCompare(b.projectName ?? ''),
        );
    }
}

const euro = (cents: number): string =>
    `${(cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// ═══════════════════════════════════════════════════════════════════════════
// Der Arbeitszustand je Projekt
// ═══════════════════════════════════════════════════════════════════════════

interface ServiceInfo {
    id: string;
    title: string;
    unit: string;
    /** LV-Preis in EURO (so steht er in der Spalte); Cent entsteht erst im Wert. */
    unitPrice: number;
    assignment: string;
    isInspection: boolean;
    isHourly: boolean;
    /** Id der Pauschalposition, die diese Position deckt — sonst `null`. */
    coveredByFlatId: string | null;
}

interface ProjectCtx {
    id: string;
    name: string;
    status: string;
    responsibleId: string;
    responsibleName: string;
    customerName: string;
    services: Map<string, ServiceInfo>;
    /** Je Pauschalposition die bereits gebuchten Monate (`FLAT-JJJJ-MM`). */
    flatBooked: Map<string, Set<string>>;
    /** Steht diese Zeile als Begehungsnachweis (Menge 0, 'NW-…') auf einer Pauschale? */
    isInspectionProof: (entry: ServiceTransaction) => boolean;
    log: ServiceTransaction[];
    startDate: string | null;
    endDate: string | null;
    contractCents: number;
    unbilledCents: number;
    backlogCents: number;
    pipelineCents: number;
    performedCents: number;
    performedLifetimeCents: number;
    billedCents: number;
    forecastCents: number;
    inspections: number;
    hours: number;
}

/**
 * Ein Projekt mit Status `planning` ist noch kein Auftrag — es ist Pipeline.
 * Ein `active` Projekt ist der laufende Auftrag, an dem Auftragsbestand haengt.
 */
const isPlanning = (status: string): boolean => status === 'planning';
const isActive = (status: string): boolean => status === 'active';

// ═══════════════════════════════════════════════════════════════════════════
// Die Engine
// ═══════════════════════════════════════════════════════════════════════════

export function buildCockpitModel(input: CockpitInput): CockpitModel {
    // Auch der Zeitraum wird nicht geglaubt, sondern gelesen: ein fehlender
    // oder halber Zustand (Altbestand im localStorage) soll das Cockpit nicht
    // zum Absturz bringen, sondern das laufende Jahr zeigen.
    const year = Math.round(num(input.period?.year)) || new Date().getUTCFullYear();
    const period: CockpitPeriod = input.period ?? { year, kind: 'year' };
    const periodMonths = periodMonthIndices(period);
    const inPeriod = new Set(periodMonths);
    const today = text(input.today).slice(0, 10);
    const employeeId = text(input.employeeId) || 'all';
    const filtered = employeeId !== 'all';

    const warnings = new WarningBag();
    for (const warning of input.forecast?.warnings ?? []) warnings.push(warning);

    // ── Nachschlagewerke, genau einmal ───────────────────────────────────
    const userById = new Map<string, UserProfile>();
    const userByName = new Map<string, UserProfile>();
    for (const user of input.users ?? []) {
        if (!user?.id) continue;
        userById.set(user.id, user);
        const key = nameKey(fullName(user));
        if (key && !userByName.has(key)) userByName.set(key, user);
    }
    const displayName = (id: string): string => {
        const user = userById.get(id);
        return (user ? fullName(user) || text(user.email) : '') || 'Ohne Zuordnung';
    };

    const rawProjects = asArray<CockpitProjectRow>(input.projects).filter(
        (row) =>
            !!row &&
            !istUebungsprojekt({
                ist_uebung: typeof row.ist_uebung === 'boolean' ? row.ist_uebung : undefined,
                project_number: text(row.project_number) || undefined,
                data: row.data ?? undefined,
            }),
    );

    // Wer ist AUSSENDIENST? Dieselbe Definition wie bisher: wer irgendwo
    // Koordinator ist, wer ein laufendes Projekt besitzt, oder wer in einer
    // Vertreterliste steht (Position 0 ist der Koordinator selbst). Die
    // Verantwortliche Planungsphase zaehlt NICHT: sie macht keine Begehungen,
    // als Aussendienstlerin markiert stuende sie mit leerer Begehungszahl in
    // der Mitarbeiterkachel.
    const fieldCoordinators = new Set<string>();
    for (const row of rawProjects) {
        const meta = row.data?.metadata ?? undefined;
        const coordinatorId = text(meta?.coordinatorId);
        if (coordinatorId) fieldCoordinators.add(coordinatorId);
        const owner = text(row.user_id);
        const status = text(row.status) || text(meta?.globalData?.status) || 'planning';
        if (owner && (isActive(status) || isPlanning(status))) fieldCoordinators.add(owner);
        for (const rep of getAusfuehrungsVertreter(meta).slice(1)) if (rep.id) fieldCoordinators.add(rep.id);
    }

    // ── Projektkontexte bauen (Stichtagsgroessen inklusive) ───────────────
    const contexts: ProjectCtx[] = [];
    for (const row of rawProjects) {
        const id = text(row.id);
        if (!id) continue;
        const meta = row.data?.metadata ?? undefined;
        const name = text(row.name) || '(ohne Namen)';
        const status = text(row.status) || text(meta?.globalData?.status) || 'planning';

        const coordinatorId = text(meta?.coordinatorId);
        const ownerId = text(row.user_id);
        let responsibleId = UNASSIGNED;
        if (coordinatorId && userById.has(coordinatorId)) responsibleId = coordinatorId;
        else if (ownerId && userById.has(ownerId)) responsibleId = ownerId;
        if (responsibleId === UNASSIGNED) {
            // Befund Nr. 7: frueher fiel genau dieses Projekt ersatzlos aus
            // allen Kacheln. Es bleibt drin und bekommt eine Warnung.
            warnings.add(
                { kind: 'unknown_responsible', projectId: id, projectName: name },
                1,
                0,
                () => 'Kein bekannter Verantwortlicher (Koordinator und Eigentümer stehen nicht in der Nutzerliste).',
            );
        }

        // Der Mitarbeiterfilter wirkt ueber die PROJEKTMENGE — damit schrumpfen
        // alle Kacheln gemeinsam (Befund Nr. 9).
        if (filtered && responsibleId !== employeeId) continue;

        const startDate = isoDay(row.start_date) ?? isoDay(meta?.globalData?.startDate);
        const endDate = isoDay(row.end_date) ?? isoDay(meta?.globalData?.endDate);
        if (!startDate) {
            warnings.add(
                { kind: 'missing_start_date', projectId: id, projectName: name },
                1,
                0,
                () => 'Kein Startdatum.',
            );
        }
        if (!endDate && isActive(status)) {
            warnings.add({ kind: 'missing_end_date', projectId: id, projectName: name }, 1, 0, () => 'Kein Enddatum.');
        }

        const rows = asArray<CockpitServiceRow>(row.project_services);
        const flatLike: FlatCoverageServiceLike[] = rows.map((service) => ({
            id: text(service.id),
            assignment: text(service.assignment) || undefined,
            lvPosition: text(service.lv_position) || undefined,
            title: text(service.title) || undefined,
            flatRate: flatRateOf(service),
            quantityUsed: num(service.quantity_used),
            quantityBilled: num(service.quantity_billed),
        }));
        // `resolveFlatCoverage` ist der Kern, den `flatCoverageHints` nur in
        // Text verpackt. Gebraucht wird hier die ZUORDNUNG (gedeckte Position ->
        // Pauschalposition), weil nur ueber die Pauschalposition die bereits
        // gebuchten Monate zu finden sind.
        const coverage = resolveFlatCoverage(flatLike);
        const log = asArray<ServiceTransaction>(meta?.globalData?.serviceLog);
        const flatBooked = new Map<string, Set<string>>();
        for (const service of flatLike) {
            if (isFlatRateService(service)) flatBooked.set(service.id, bookedFlatMonths(log, service.id));
        }

        const services = new Map<string, ServiceInfo>();
        let contractCents = 0;
        let backlogCents = 0;
        for (const service of rows) {
            const serviceId = text(service.id);
            if (!serviceId) continue;
            const assignment = text(service.assignment) || 'additional';
            const unit = text(service.unit);
            const unitPrice = num(service.unit_price);
            services.set(serviceId, {
                id: serviceId,
                title: text(service.title) || 'Leistung',
                unit,
                unitPrice,
                assignment,
                isInspection: assignment === 'inspection',
                isHourly: isHourlyUnit(unit),
                coveredByFlatId: coverage.get(serviceId) ?? null,
            });

            // NEP zaehlt erst, was gebucht ist (nepPosition.contractedQuantityOfRow).
            const contracted = contractedQuantityOfRow(service);
            contractCents += entryCents(contracted, unitPrice);

            // Eine beauftragte Position ohne Preis erzeugt Arbeit und Termine,
            // aber nie Umsatz — im alten Cockpit hiess die Meldung schon
            // 'zero_price' und ist beim Umbau verlorengegangen.
            if (contracted > 0 && unitPrice === 0) {
                warnings.add(
                    { kind: 'zero_price', projectId: id, projectName: name },
                    1,
                    0,
                    (count) => `Beauftragte Menge ohne Preis: ${count} Position(en) mit 0 €.`,
                );
            }

            // AUFTRAGSBESTAND — was beauftragt, aber noch nicht erbracht ist.
            // Nur am laufenden Projekt: an einem beendeten steht nichts mehr aus.
            if (isActive(status)) {
                backlogCents += entryCents(Math.max(0, contracted - num(service.quantity_used)), unitPrice);
            }
        }

        // NOCH ABZURECHNEN — Stichtag heute. GENAU die Zahl, die die
        // Buchhaltungsansicht anzeigt: `getProjectFinancials(...).displayOpen`
        // (projectLv), nicht die Summe der rohen `openQuantity`.
        //
        // Der Unterschied ist die ALT-Deckung. Eine aus einer Alt-Rechnung
        // nachgetragene Menge traegt `quantityBilled` 0 und steht in
        // `openQuantity` als offen — gefordert wurde sie aber laengst. Die
        // Buchhaltung zieht sie deshalb in `displayOpen` ab; rechnete das
        // Cockpit daneben mit `openQuantity`, meldete es fuer dieselben
        // Projekte dauerhaft mehr „noch abzurechnen" als die Ansicht, aus der
        // tatsaechlich abgerechnet wird. Zwei Zahlen fuer eine Aussage.
        //
        // Ein Projekt in Planung hat nichts erbracht und damit nichts offen.
        const lvRow: ProjectRowLike = {
            id,
            name,
            status,
            project_services: rows,
            ...(meta ? { data: { metadata: meta } } : {}),
        };
        const unbilledCents = isPlanning(status) ? 0 : centsFromEuro(getProjectFinancials(lvRow).displayOpen);

        contexts.push({
            id,
            name,
            status,
            responsibleId,
            responsibleName: responsibleId === UNASSIGNED ? 'Ohne Zuordnung' : displayName(responsibleId),
            customerName: (text(row.customer_name) || text(meta?.customerName)).trim() || NO_CUSTOMER,
            services,
            flatBooked,
            isInspectionProof: inspectionProofMatcher(flatLike),
            log,
            startDate,
            endDate,
            contractCents,
            unbilledCents,
            backlogCents,
            pipelineCents: isPlanning(status) ? contractCents : 0,
            performedCents: 0,
            performedLifetimeCents: 0,
            billedCents: 0,
            forecastCents: 0,
            inspections: 0,
            hours: 0,
        });
    }

    const contextById = new Map<string, ProjectCtx>(contexts.map((ctx) => [ctx.id, ctx]));

    // ── Zeitreihen ───────────────────────────────────────────────────────
    const months: MonthCell[] = Array.from({ length: 12 }, () => emptyCell());
    const prevDecember = emptyCell();
    const performedDetails: PerformedDetail[] = [];
    const billedDetails: BilledDetail[] = [];

    /** Je Verantwortlichem: erbracht auf seinen Projekten, Monat fuer Monat. */
    const byResponsible = new Map<string, MonthSeries>();
    const inspectionsByResponsible = new Map<string, MonthSeries>();
    const prevDecByResponsible = new Map<string, number>();
    /** Je Ausfuehrendem: selbst erbracht, Monat fuer Monat. */
    const bySelf = new Map<string, MonthSeries>();
    const series = (map: Map<string, MonthSeries>, key: string): MonthSeries => {
        let row = map.get(key);
        if (!row) {
            row = zeroMonths();
            map.set(key, row);
        }
        return row;
    };

    const serviceTypes = new Map<string, ServiceTypeRow>();
    const serviceType = (key: string): ServiceTypeRow => {
        let row = serviceTypes.get(key);
        if (!row) {
            row = { key, label: assignmentLabel(key, 'lang'), performedCents: 0, forecastCents: 0, entryCount: 0 };
            serviceTypes.set(key, row);
        }
        return row;
    };

    // ── ERBRACHT: ein Durchgang ueber alle Protokolle ────────────────────
    for (const ctx of contexts) {
        const responsibleSeries = series(byResponsible, ctx.responsibleId);
        const responsibleInspections = series(inspectionsByResponsible, ctx.responsibleId);

        for (const entry of ctx.log) {
            const serviceId = text(entry?.serviceId);
            // 'contract_change' ist ein Nachtragsvermerk, keine Leistung.
            if (!serviceId || serviceId === 'contract_change') continue;
            // Verbuchungen ('BU-', 'MB-', Text „manuelle Verbuchung"), Verzicht
            // ('VZ-') und Ruecknahme ('RN-') sind keine Arbeit.
            if (!isWorkEntry(entry)) continue;

            const rawQuantity: unknown = entry.quantity;
            if (typeof rawQuantity === 'string' && rawQuantity.trim() !== '') {
                // Zaehlt trotzdem mit: im jsonb stehen Mengen seit jeher auch
                // als String. Gemeldet wird sie, damit der Bestand sauber wird.
                warnings.add(
                    { kind: 'string_quantity', projectId: ctx.id, projectName: ctx.name },
                    1,
                    0,
                    (count) => `${count} Protokolleintrag/Einträge mit der Menge als Text statt als Zahl.`,
                );
            }
            // ALT-Uebernahme: `quantity` ist die BELEGmenge, angewandt wurde oft
            // weniger. Der Leistungsstand kennt nur `appliedUsed` — und nur
            // dann, wenn dort wirklich eine Zahl steht (cockpitLogic.altQuantity).
            const quantity = altQuantity(entry);
            // DIESELBE Bedingung wie in `altQuantity`, und zwar buchstaeblich
            // dieselbe Funktion: nur wenn die Zeile wirklich `appliedUsed`
            // statt der Belegmenge genommen hat, ist das ein
            // erklaerungsbeduerftiger Sonderfall fuer die Detailzeile. Stand
            // die Bedingung hier ein zweites Mal ausgeschrieben, erklaerte der
            // Hinweis irgendwann eine Menge, die gar nicht genommen wurde.
            const usedAppliedQuantity = usesAppliedQuantity(entry);
            let note: string | undefined = usedAppliedQuantity
                ? 'Alt-Übernahme, angewandte Menge'
                : undefined;

            if (quantity < 0) {
                // Faellt NICHT mehr still weg (Befund: „Stornos werden
                // verworfen"). Die Summe ruehrt sie nicht an — eine negative
                // Leistungsmenge ist ein Datenfehler, keine Gutschrift; die
                // Gutschrift steht auf einem Beleg und kommt ueber ABGERECHNET.
                warnings.add(
                    { kind: 'negative_quantity', projectId: ctx.id, projectName: ctx.name },
                    1,
                    0,
                    (count) => `${count} Protokolleintrag/Einträge mit negativer Menge — nicht gewertet.`,
                );
                continue;
            }
            if (quantity === 0) {
                // NACHWEISZEILE: im Pauschalprojekt steht die Begehung seit
                // 30.09.2026 mit Menge 0 auf der Pauschale, die
                // Begehungsposition bekommt nichts mehr. Sie ist eine Begehung
                // (ein Termin), aber kein Geld — ohne diesen Zweig fiele sie aus
                // jeder Begehungszahl, und das Cockpit zaehlte ab der
                // Umstellung keine Begehung dieses Projekts mehr.
                if (ctx.isInspectionProof(entry)) {
                    const proofDay = calendarDay(performedDateIso(entry));
                    if (proofDay && proofDay.year === year - 1 && proofDay.month === 11) prevDecember.inspections += 1;
                    if (proofDay && proofDay.year === year) {
                        months[proofDay.month].inspections += 1;
                        responsibleInspections[proofDay.month] += 1;
                        if (inPeriod.has(proofDay.month)) ctx.inspections += 1;
                    }
                }
                continue;
            }

            const service = ctx.services.get(serviceId);
            if (!service) {
                // Geloeschte oder umgezogene Position: frueher still 0 EUR
                // (Befund Nr. 8), jetzt sichtbar.
                warnings.add(
                    { kind: 'unknown_service', projectId: ctx.id, projectName: ctx.name },
                    1,
                    0,
                    (count) => `${count} Protokolleintrag/Einträge ohne passende LV-Position — nicht bewertbar.`,
                );
                continue;
            }

            const iso = performedDateIso(entry);
            const day = calendarDay(iso);
            let cents = entryCents(quantity, service.unitPrice);

            // MIGRATIONS-SAMMELBUCHUNG: kuenstlich datierter Altbestand. Nie
            // Monatsumsatz, immer sichtbar.
            if (isMigrationBulkBackfill(entry)) {
                if (day && day.year === year) {
                    warnings.add(
                        { kind: 'legacy_backfill', projectId: ctx.id, projectName: ctx.name },
                        1,
                        cents,
                        (count, sum) =>
                            `${count} Sammelnachtrag/Sammelnachträge aus der Datenübernahme (${euro(sum)}) — künstlich datiert, nicht als Monatsleistung gewertet.`,
                    );
                }
                continue;
            }

            // MONATSPAUSCHALE: ist die Position in DIESEM Monat durch eine
            // gebuchte Pauschale gedeckt, zaehlt nur die Pauschale. Der
            // Leistungsstand laeuft weiter (Begehung bleibt eine Begehung),
            // der GELDSTAND steht still.
            if (service.coveredByFlatId) {
                const key = flatMonthKey(iso);
                // NUR EINE PAUSCHALE MIT PREIS DECKT AB — dieselbe Grenze wie
                // im Forecast (dort `monthCents > 0`). Eine Pauschalposition
                // zu 0 EUR bringt in ihrem Monat kein Geld; legte sie die
                // Begehung trotzdem still, fiele der Monat ERSATZLOS aus
                // ERBRACHT, statt bloss nicht doppelt zu zaehlen — und der
                // Forecast, der diese Pauschale nie als zahlend gefuehrt hat,
                // erwartete fuer denselben Monat weiter die Begehung.
                const flatPrice = ctx.services.get(service.coveredByFlatId)?.unitPrice ?? 0;
                if (key && flatPrice > 0 && ctx.flatBooked.get(service.coveredByFlatId)?.has(key)) {
                    cents = 0;
                    note = 'durch Monatspauschale gedeckt';
                }
            }

            // Stunden: ein Eintrag ist EIN Termin, nicht so viele Termine wie
            // Stunden (Befund Nr. 12). Stueck: die Menge ist die Zahl der Termine.
            const inspections = service.isInspection ? (service.isHourly ? 1 : quantity) : 0;

            // AUSFUEHRENDER: id zuerst, dann der Name ('system'-Stubs tragen
            // den echten Namen), sonst niemand — und eine Warnung.
            const performerRaw = text(entry.performerId);
            let performerId: string | null = null;
            if (performerRaw && performerRaw !== 'system' && userById.has(performerRaw)) performerId = performerRaw;
            else {
                const byName = userByName.get(nameKey(entry.performerName));
                if (byName) performerId = byName.id;
            }
            if (!performerId) {
                warnings.add(
                    { kind: 'unknown_performer', projectId: ctx.id, projectName: ctx.name },
                    1,
                    0,
                    (count) => `${count} Protokolleintrag/Einträge ohne bekannten Ausführenden.`,
                );
            }

            ctx.performedLifetimeCents += cents;

            if (day && day.year === year - 1 && day.month === 11) {
                prevDecember.performedCents += cents;
                prevDecember.inspections += inspections;
                prevDecByResponsible.set(ctx.responsibleId, (prevDecByResponsible.get(ctx.responsibleId) ?? 0) + cents);
            }
            if (!day || day.year !== year) continue;

            const monthIndex = day.month;
            months[monthIndex].performedCents += cents;
            months[monthIndex].inspections += inspections;
            responsibleSeries[monthIndex] += cents;
            responsibleInspections[monthIndex] += inspections;
            if (performerId) series(bySelf, performerId)[monthIndex] += cents;

            // DETAILZEILE FUER ALLE ZWOELF MONATE, nicht nur fuer den
            // Zeitraum: das Chart ist auf jedem Balken anklickbar. Stand im
            // Juli und klickte man auf Maerz, war der Drilldown leer, obwohl
            // der Balken Geld zeigte. Die KACHELN bleiben der Zeitraum — alles
            // unterhalb dieses `continue` summiert weiterhin nur `periodMonths`.
            performedDetails.push({
                month: monthIndex,
                date: iso ?? '',
                projectId: ctx.id,
                projectName: ctx.name,
                serviceId,
                serviceTitle: service.title,
                unit: service.unit,
                performerId,
                performerName: performerId ? displayName(performerId) : text(entry.performerName),
                responsibleId: ctx.responsibleId,
                quantity,
                cents,
                reference: text(entry.bookingReference),
                ...(note ? { note } : {}),
            });

            if (!inPeriod.has(monthIndex)) continue;
            ctx.performedCents += cents;
            ctx.inspections += inspections;
            if (service.isHourly) ctx.hours += quantity;
            const type = serviceType(service.assignment);
            type.performedCents += cents;
            type.entryCount += 1;
        }
    }

    // ── ABGERECHNET und FORDERUNGEN: ein Durchgang ueber die Belege ──────
    let receivablesOpenCents = 0;
    let receivablesOverdueCents = 0;
    let receivablesOverdueCount = 0;

    for (const invoice of input.invoices ?? []) {
        if (!invoice) continue;
        // Entwuerfe, Freigaben und das Ausstellen selbst sind kein Umsatz —
        // dieselbe Grenze wie `invoiceStats` (isIssuedInvoice). Der stornierte
        // URSPRUNGSbeleg bleibt bewusst drin: er und sein negativer Storno
        // ergeben null, ihn zusaetzlich auszublenden minderte doppelt.
        if (!isIssuedInvoice(invoice)) continue;

        const projectId = text(invoice.primaryProjectId) || null;
        const ctx = projectId ? contextById.get(projectId) : undefined;
        const key = monthKey(invoice.invoiceDate);
        const invoiceYear = key ? Number(key.slice(0, 4)) : null;
        const invoiceMonth = key ? Number(key.slice(5, 7)) - 1 : null;
        if (!ctx) {
            // KEIN PROJEKT ZU FINDEN — zwei Faelle, eine Konsequenz.
            //
            // Unter einem MITARBEITERFILTER ist das der Normalfall: der Beleg
            // gehoert zu einem Projekt, das der Filter ausgeschlossen hat. Er
            // faellt heraus und ist keine Meldung wert, sonst stuenden bei
            // jedem Filterwechsel Dutzende Warnungen da.
            if (filtered) continue;
            // OHNE Filter fehlt das Projekt wirklich — geloescht, Uebung,
            // fremde Firma. Frueher fiel so ein Beleg still aus ABGERECHNET
            // und aus den offenen Forderungen; die Kachel war dann niedriger
            // als die Belegliste der Buchhaltung, ohne dass irgendwo stand,
            // warum. Jetzt zaehlt er wie ein Beleg ohne Projekt in die
            // FIRMENsumme und wird gemeldet.
            //
            // GEMELDET aber nur, wenn der Beleg im ANGEZEIGTEN Jahr liegt.
            // Die Warnungsliste steht neben Kacheln, die ausschliesslich
            // `period.year` zeigen; eine Meldung ueber einen Beleg vom
            // März 2026 unter der Jahreszahl 2025 schickt den Leser in ein
            // Jahr, das gar nicht offen ist. Gezählt wird der Beleg
            // weiterhin dort, wo er hingehört — nur eben stumm.
            if (invoiceYear === year) {
                warnings.add(
                    projectId
                        ? { kind: 'invoice_without_project', projectId }
                        : { kind: 'invoice_without_project' },
                    1,
                    revenueNetCents(invoice),
                    projectId
                        ? (count, sum) =>
                            `${count} Beleg(e) ohne auffindbares Projekt (${euro(sum)}) — zählen in die Firmensumme, nicht in eine Mitarbeiterzahl.`
                        : (count, sum) =>
                            `${count} Beleg(e) ohne Projektbezug (${euro(sum)}) — zählen in die Firmensumme, nicht in eine Mitarbeiterzahl.`,
                );
            }
        }

        const net = revenueNetCents(invoice);
        if (invoiceYear === null || invoiceMonth === null) {
            // KEIN BRAUCHBARES RECHNUNGSDATUM. `monthKey` weist leere,
            // abgeschnittene und unmoegliche Tage zurueck ('2026-02-31'),
            // statt wie `new Date(...)` still in den Maerz zu rutschen — der
            // Beleg hat also keinen Monat und kann in keinen Balken.
            //
            // Frueher fiel er hier wortlos aus ABGERECHNET: die Kachel stand
            // unter der Summe der Buchhaltung, und niemand konnte sehen, um
            // welchen Beleg es ging. Jetzt steht er mit Anzahl und Wert in der
            // Warnliste. Die FORDERUNGEN unten laufen bewusst weiter: sie
            // haengen am Stichtag, nicht am Monat.
            warnings.add(
                ctx
                    ? { kind: 'invoice_without_date', projectId: ctx.id, projectName: ctx.name }
                    : { kind: 'invoice_without_date' },
                1,
                net,
                (count, sum) =>
                    `${count} Beleg(e) ohne gültiges Rechnungsdatum (${euro(sum)}) — nicht zugeordnet.`,
            );
        } else {
            if (invoiceYear === year - 1 && invoiceMonth === 11) prevDecember.billedCents += net;
            if (invoiceYear === year) {
                months[invoiceMonth].billedCents += net;
                // Wie bei ERBRACHT: die Belegzeile entsteht fuer ALLE zwoelf
                // Monate, weil der Drilldown an jedem Balken haengt. Die
                // PROJEKTzeile (und damit Kachel und Kundentabelle) bleibt auf
                // den Zeitraum beschraenkt.
                billedDetails.push({
                    month: invoiceMonth,
                    date: text(invoice.invoiceDate).slice(0, 10),
                    invoiceId: text(invoice.id),
                    invoiceNumber: text(invoice.invoiceNumber),
                    documentType: invoice.documentType,
                    projectId,
                    projectName: ctx?.name ?? '—',
                    customerName: text(invoice.customerName) || text(invoice.customerSnapshot?.name) || '—',
                    cents: net,
                });
                if (inPeriod.has(invoiceMonth) && ctx) ctx.billedCents += net;
            }
        }

        // OFFENE FORDERUNGEN — Stichtag heute, unabhaengig vom Zeitraum.
        if (!OWING_STATUSES.has(effectiveInvoiceStatus(invoice, today))) continue;
        const open = openAmountCents(invoice);
        if (open <= 0) continue;
        receivablesOpenCents += open;
        // `daysPastDue` statt `daysOverdue`: ein bestrittener Beleg wird nicht
        // gemahnt, ist aber trotzdem seit Monaten faellig.
        if (daysPastDue(invoice, today) > 0) {
            receivablesOverdueCents += open;
            receivablesOverdueCount += 1;
        }
    }

    // ── FORECAST (aus WP2) in die Monate und Zeilen ──────────────────────
    const forecastByProject = input.forecast?.byProject ?? {};
    const forecastByService = input.forecast?.byService ?? {};
    for (const ctx of contexts) {
        const projectSeries = forecastByProject[ctx.id];
        if (Array.isArray(projectSeries)) {
            for (let index = 0; index < 12; index += 1) {
                const value = Math.round(num(projectSeries[index]));
                months[index].forecastCents += value;
                if (inPeriod.has(index)) ctx.forecastCents += value;
            }
        }
        const perService = forecastByService[ctx.id];
        if (perService) {
            for (const [serviceId, serviceSeries] of Object.entries(perService)) {
                if (!Array.isArray(serviceSeries)) continue;
                const assignment = ctx.services.get(serviceId)?.assignment ?? 'additional';
                const row = serviceType(assignment);
                for (const index of periodMonths) row.forecastCents += Math.round(num(serviceSeries[index]));
            }
        }
    }

    // ── Externe Offsets: nur in der Firmensicht ──────────────────────────
    // Sie sind extern getrackter Umsatz ohne Projekt und ohne Mitarbeiter.
    // Unter einem Filter wuerden sie einer Person Umsatz zuschreiben, den sie
    // nicht gemacht hat.
    //
    // MIT DETAILZEILE, seit der Drilldown an jedem Balken haengt: ein Offset
    // hob den Balken, tauchte aber in keiner Zeile auf — die Detailsumme war
    // dann kleiner als der Balken darueber, und niemand konnte sehen, woher
    // die Differenz kam. Die Zeile nennt `kpi_config` als Quelle.
    //
    // ABSICHTLICH NICHT in den Mitarbeiterzeilen: ein Offset ist eine
    // FIRMENgroesse ohne Verantwortlichen. Waere er dort, addierte sich die
    // Team-Tabelle nicht mehr zur Kachel oder schriebe „Ohne Zuordnung"
    // Umsatz zu, den niemand erbracht hat. Deshalb traegt die Zeile
    // `responsibleId: UNASSIGNED` nur als Platzhalter des Vertrags und fliesst
    // in keine Reihe von `byResponsible`.
    if (!filtered) {
        const offsets = input.offsetsCents ?? [];
        for (let index = 0; index < 12; index += 1) {
            const value = Math.round(num(offsets[index]));
            if (value === 0) continue;
            months[index].performedCents += value;
            performedDetails.push({
                month: index,
                date: `${String(year).padStart(4, '0')}-${String(index + 1).padStart(2, '0')}-${String(daysInMonth(year, index)).padStart(2, '0')}`,
                projectId: '',
                projectName: 'Extern/Korrektur',
                serviceId: '',
                serviceTitle: 'Externer Umsatz (kpi_config)',
                unit: '',
                performerId: null,
                performerName: 'Extern',
                responsibleId: UNASSIGNED,
                quantity: 1,
                cents: value,
                reference: 'OFFSET',
                note: 'externer Umsatz aus kpi_config',
            });
        }
    }

    // ── Kacheln: Summe der Zeitraummonate, Stichtagswerte daneben ────────
    let performedCents = 0;
    let billedCents = 0;
    let forecastCents = 0;
    let inspections = 0;
    for (const index of periodMonths) {
        const cell = months[index];
        if (!cell) continue;
        performedCents += cell.performedCents;
        billedCents += cell.billedCents;
        forecastCents += cell.forecastCents;
        inspections += cell.inspections;
    }

    let unbilledCents = 0;
    let backlogCents = 0;
    let pipelineCents = 0;
    for (const ctx of contexts) {
        unbilledCents += ctx.unbilledCents;
        backlogCents += ctx.backlogCents;
        pipelineCents += ctx.pipelineCents;
    }

    const tiles: CockpitTiles = {
        performedCents,
        billedCents,
        unbilledCents,
        receivablesOpenCents,
        receivablesOverdueCents,
        receivablesOverdueCount,
        forecastCents,
        // Ungedeckelt und `null` statt 0 bei fehlendem Forecast: „0 %" waere
        // die Aussage „nichts erreicht", richtig ist „keine Erwartung da".
        realisationPct: forecastCents > 0 ? (performedCents / forecastCents) * 100 : null,
        backlogCents,
        pipelineCents,
        inspections,
    };

    // ── Team ─────────────────────────────────────────────────────────────
    const lastMonth = periodMonths[periodMonths.length - 1] ?? 11;
    const projectCount = new Map<string, number>();
    const forecastByResponsible = new Map<string, number>();
    for (const ctx of contexts) {
        projectCount.set(ctx.responsibleId, (projectCount.get(ctx.responsibleId) ?? 0) + 1);
        forecastByResponsible.set(
            ctx.responsibleId,
            (forecastByResponsible.get(ctx.responsibleId) ?? 0) + ctx.forecastCents,
        );
    }

    // AUSLASTUNG = max(IST, PLAN) je Monat (Betreiberregel). PLAN kommt aus
    // dem Forecast (Turnus/Wochentage, Vertreter eingeschlossen), IST aus den
    // Protokollen aller Projekte nach AUSFUEHRENDEM (cockpitWorkload) — beide
    // firmenweit gerechnet, damit unter dem Mitarbeiterfilter auch Arbeit auf
    // fremden Projekten bei der gewaehlten Person bleibt.
    const workloadPlan = input.forecast?.workloadMinutes ?? {};
    const workloadIst = input.forecast?.workloadIstMinutes ?? {};
    const hasMinutes = (series: unknown): boolean =>
        Array.isArray(series) && series.some((v) => num(v) > 0);

    const employeeIds: string[] = [...userById.keys()];
    // „Ohne Zuordnung" auch dann, wenn dort NUR Auslastung steht: Stunden
    // unbekannter (fremder, ausgeschiedener) Ausfuehrender fallen sonst still
    // weg — oder, wie vorher, dem Koordinator zu. Unter dem Filter nicht:
    // dort gilt nur die gewaehlte Person.
    const unassignedLoad = !filtered
        && (hasMinutes(workloadPlan[UNASSIGNED]) || hasMinutes(workloadIst[UNASSIGNED]));
    if (projectCount.has(UNASSIGNED) || byResponsible.has(UNASSIGNED) || unassignedLoad) employeeIds.push(UNASSIGNED);
    const employees: EmployeeRow[] = employeeIds.map((id) => {
        const monthly = byResponsible.get(id) ?? zeroMonths();
        const inspectionSeries = inspectionsByResponsible.get(id) ?? zeroMonths();
        const self = bySelf.get(id) ?? zeroMonths();
        let performed = 0;
        let performedSelf = 0;
        let employeeInspections = 0;
        for (const index of periodMonths) {
            performed += monthly[index] ?? 0;
            performedSelf += self[index] ?? 0;
            employeeInspections += inspectionSeries[index] ?? 0;
        }
        // Vormonat des LETZTEN Zeitraummonats, ueber die Jahresgrenze hinweg:
        // im Januar ist das der Dezember des Vorjahres, nicht der Dezember
        // desselben Jahres (Befund Nr. 10: Trend +100 % in jedem Januar).
        const prevMonthPerformedCents =
            lastMonth === 0 ? (prevDecByResponsible.get(id) ?? 0) : (monthly[lastMonth - 1] ?? 0);
        // AUSLASTUNG FOLGT DEM FILTER. Der Forecast (WP2) rechnet immer die
        // ganze Firma — er kennt den Mitarbeiterfilter gar nicht. Wurde seine
        // Minutenreihe hier ungefiltert durchgereicht, stand unter dem Filter
        // „Anna" bei Bernd weiterhin seine volle Auslastung, waehrend seine
        // Projekte, sein Erbracht und sein Forecast auf 0 standen: eine Zeile,
        // die zu 100 % ausgelastet und gleichzeitig ohne Arbeit war. Der
        // GEWAEHLTE Mitarbeiter behaelt seine Minuten, alle anderen nicht.
        const hidden = filtered && id !== employeeId;
        const plan = hidden ? undefined : workloadPlan[id];
        const ist = hidden ? undefined : workloadIst[id];
        const planSeries = Array.isArray(plan) ? plan.map((v) => Math.round(num(v))) : zeroMonths();
        const istSeries = Array.isArray(ist) ? ist.map((v) => Math.round(num(v))) : zeroMonths();
        return {
            id,
            name: id === UNASSIGNED ? 'Ohne Zuordnung' : displayName(id),
            projectCount: projectCount.get(id) ?? 0,
            performedCents: performed,
            performedSelfCents: performedSelf,
            forecastCents: forecastByResponsible.get(id) ?? 0,
            inspections: employeeInspections,
            avgPerInspectionCents: employeeInspections > 0 ? Math.round(performed / employeeInspections) : null,
            monthlyPerformed: [...monthly],
            prevMonthPerformedCents,
            workloadMinutes: combineWorkload(istSeries, planSeries).minutes,
            workloadIstMinutes: istSeries,
            workloadPlanMinutes: planSeries,
            isFieldCoordinator: fieldCoordinators.has(id),
        };
    });
    employees.sort((a, b) => b.performedCents - a.performedCents || a.name.localeCompare(b.name));

    // ── Projekte und Kunden ──────────────────────────────────────────────
    const todayMs = Date.parse(`${today || '1970-01-01'}T00:00:00Z`);
    const projects: ProjectRow[] = contexts.map((ctx) => ({
        id: ctx.id,
        name: ctx.name,
        status: ctx.status,
        responsibleId: ctx.responsibleId,
        responsibleName: ctx.responsibleName,
        customerName: ctx.customerName,
        contractCents: ctx.contractCents,
        performedCents: ctx.performedCents,
        performedLifetimeCents: ctx.performedLifetimeCents,
        billedCents: ctx.billedCents,
        unbilledCents: ctx.unbilledCents,
        backlogCents: ctx.backlogCents,
        forecastCents: ctx.forecastCents,
        inspections: ctx.inspections,
        hours: ctx.hours,
        startDate: ctx.startDate,
        endDate: ctx.endDate,
        daysRemaining:
            ctx.endDate && Number.isFinite(todayMs)
                ? Math.round((Date.parse(`${ctx.endDate}T00:00:00Z`) - todayMs) / 86_400_000)
                : null,
    }));
    projects.sort((a, b) => b.performedCents - a.performedCents || a.name.localeCompare(b.name));

    const customerMap = new Map<string, CustomerRow>();
    for (const ctx of contexts) {
        let row = customerMap.get(ctx.customerName);
        if (!row) {
            row = { name: ctx.customerName, performedCents: 0, billedCents: 0, projectCount: 0 };
            customerMap.set(ctx.customerName, row);
        }
        row.performedCents += ctx.performedCents;
        row.billedCents += ctx.billedCents;
        row.projectCount += 1;
    }
    const customers = [...customerMap.values()].sort(
        (a, b) => b.performedCents - a.performedCents || a.name.localeCompare(b.name),
    );

    performedDetails.sort((a, b) => a.date.localeCompare(b.date));
    billedDetails.sort((a, b) => a.date.localeCompare(b.date));

    return {
        period,
        months,
        prevDecember,
        periodMonths,
        tiles,
        employees,
        projects,
        customers,
        serviceTypes: [...serviceTypes.values()].sort(
            (a, b) => b.performedCents - a.performedCents || a.label.localeCompare(b.label),
        ),
        performedDetails,
        billedDetails,
        warnings: warnings.list(),
    };
}
